import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  agentToolsApi,
  type MCPToolSharing,
} from "../../../services/agent-tools-service";
import { MCP_TOOLS_QUERY_KEY } from "./useMCPTools";

type ApiError = {
  response?: {
    data?: { error?: { message?: string }; detail?: string; message?: string };
  };
  message?: string;
};

const extractErrorMessage = (error: ApiError, fallback: string): string => {
  const data = error.response?.data;
  return (
    data?.error?.message ||
    data?.detail ||
    data?.message ||
    error.message ||
    fallback
  );
};

type Variables = {
  connectionId: string;
  toolId: string;
  sharing: MCPToolSharing;
};

type Options = {
  onSuccess?: () => void;
};

export const useUpdateToolSharing = (options: Options = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ connectionId, toolId, sharing }: Variables) =>
      agentToolsApi.updateMCPToolSharing(connectionId, toolId, sharing),
    onSuccess: (_data, { connectionId }) => {
      toast.success(t("mcpPage.mutation.toolAccessUpdated"));
      queryClient.invalidateQueries({
        queryKey: [...MCP_TOOLS_QUERY_KEY, connectionId],
      });
      options.onSuccess?.();
    },
    onError: (error: ApiError) => {
      toast.error(extractErrorMessage(error, t("mcpPage.mutation.toolAccessFailed")));
    },
  });
};
